import { PropertyHelper } from '@/helpers/property.helper';
import { UserHelper } from '@/helpers/user.helper';
import { Utils } from '@/helpers/utils';
import { AlertService } from '@/services/alert.service';
import { Component, EventEmitter, Inject, Input, LOCALE_ID, OnDestroy, OnInit, Output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { SafeUrl } from '@angular/platform-browser';
import { Document, DocumentIdentifier, DocumentSummary, RealProperty, VaultService } from '@eemo/api-client';
import { Subscription } from 'rxjs';
import { FileSizePipe } from '../../extensions/fileSizePipe';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { DocUpdateDialogComponent } from '../doc-update-dialog/doc-update-dialog.component';

import { TooltipDirective } from '@/directives/tooltip/tooltip.directive';
import { ActionService } from '@/services/action.service';
import { NgFor, NgIf, NgTemplateOutlet } from '@angular/common';

interface PropertyDocCardData {
  doc: Document | DocumentSummary;
  docIdentifierName: string | null;
  docDate: string;
  tooltip: string;
  iconClass: string;
  url: SafeUrl;
  filename: string;
  viewable: boolean;
  canUpdate: boolean;
  canDelete: boolean;
}

@Component({
  selector: 'app-property-doc-card',
  templateUrl: './property-doc-card.component.html',
  styleUrls: ['./property-doc-card.component.scss'],
  standalone: true,
  imports: [
    NgIf,
    NgFor,
    NgTemplateOutlet,
    MatIconModule,
    MatButtonModule,
    MatMenuModule,
    FileSizePipe,
    TooltipDirective
  ]
})
export class PropertyDocCardComponent implements OnInit, OnDestroy {
  @Input() prop: RealProperty;
  @Input() title: string;
  @Input() docs: Document[] | DocumentSummary[];
  //active the "lightMode" with fewer information and callbacke action
  @Input() lightMode = false;
  @Output() docUpdated = new EventEmitter<boolean>();
  @Output() docDeleted = new EventEmitter<Document>();

  docsData: PropertyDocCardData[] = [];
  canUpdateProperty = false;

  tokenSub = new Subscription();
  deleteSub = new Subscription();

  constructor(
    private dialog: MatDialog,
    private propHelper: PropertyHelper,
    private userHelper: UserHelper,
    private vaultService: VaultService,
    private alertService: AlertService,
    private actionService: ActionService,
    @Inject(LOCALE_ID) public locale: string
  ) {}

  ngOnInit() {
    this.canUpdateProperty = this.userHelper.canUpdateProperty(this.prop);
    this.docs.forEach((doc) => {
      this.docsData.push({
        doc,
        docIdentifierName:
          doc.identifier && doc.identifier !== DocumentIdentifier.NONE
            ? this.propHelper.getDocumentIdentifierName(doc.identifier)
            : null,
        docDate: doc.dateCreate ? Utils.formatDateMonth(doc.dateCreate, this.locale) : '',
        tooltip: this.propHelper.getFileTooltipFromMimeType(doc.mimeType),
        iconClass: this.propHelper.getFileIconFromMimeType(doc.mimeType),
        url: this.propHelper.getDocumentUrl(doc),
        filename: this.propHelper.getDocumentFilename(doc),
        viewable: this.propHelper.isDocumentViewable(doc),
        canUpdate: this.userHelper.canUpdateDocument(this.prop, doc),
        canDelete: this.userHelper.canDeleteDocument(this.prop, doc)
      });
    });
  }

  ngOnDestroy(): void {
    this.tokenSub.unsubscribe();
    this.deleteSub.unsubscribe();
  }

  ligthModeOnDelete(doc: Document) {
    this.docDeleted.emit(doc);
  }

  viewDoc(row: PropertyDocCardData) {
    this.actionService.viewDoc(row.doc);
  }

  edit(docToEdit: Document) {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const dialogRef = this.dialog.open(DocUpdateDialogComponent, {
        // width: '20rem'
        data: { doc: docToEdit, prop: this.prop }
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.docUpdated.emit(true);
        }
      });
    }
  }

  confirmDelete(doc: Document) {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        // width: '25rem',
        data: {
          message: 'Confirmez-vous la suppression du document "' + doc.name + '" ?'
        }
      });
      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.doDelete(doc);
        }
      });
    }
  }

  doDelete(doc: Document) {
    this.deleteSub = this.vaultService.deleteDocument(doc.uid).subscribe(() => {
      this.alertService.success('Suppression effectuée avec succès');
      this.docUpdated.emit(true);
    });
  }
}
